import { baseApiClient as api } from './baseService';
import type {
  BlobSinkActionTargetSystem,
  BlobSinkConfigSchema,
} from './generated/blobSinkConfigService';

const BLOB_SINK_PROMPT_PREVIEW_TAG = 'blob-sink-prompt-preview' as const;

export type BlobSinkPromptPreviewRunStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED';

export type BlobSinkPromptPreviewResultStatus = 'SUCCESS' | 'FAILED';

export type BlobSinkPromptPreviewTargetSystem =
  | BlobSinkActionTargetSystem
  | 'DROPBOX';

export interface SmartAdvocatePreviewMetadata {
  document_direction_id: number;
  document_origin_id: number;
  category_id: number | null;
  category_name: string | null;
  subcategory_id: number | null;
  subcategory_name: string | null;
  from_unique_contact_id: number | null;
  from_contact_name: string | null;
  to_unique_contact_id: number | null;
  to_contact_name: string | null;
  description: string;
  comments?: string | null;
  overall_confidence: number;
  reasoning: string;
  degraded: boolean;
  degraded_reason?: string | null;
}

export interface NeosPreviewMetadata {
  doc_type_id: string | null;
  doc_type_title: string | null;
  document_category_id: string | null;
  document_category_title: string | null;
}

export type BlobSinkPromptPreviewMetadata =
  | SmartAdvocatePreviewMetadata
  | NeosPreviewMetadata;

export interface BlobSinkPromptPreviewResultSchema {
  id: string;
  run_id: string;
  action_id: string | null;
  matter_id: string;
  blob_id: string;
  status: BlobSinkPromptPreviewResultStatus;
  current_path: string | null;
  current_name: string | null;
  proposed_path: string | null;
  proposed_name: string | null;
  proposed_metadata: BlobSinkPromptPreviewMetadata | null;
  reasoning: string | null;
  confidence: number | null;
  error_message: string | null;
  created_at: string;
  modified_at: string;
}

export interface BlobSinkPromptPreviewRunSchema {
  id: string;
  firm_id: string;
  config_id: string;
  base_run_id: string | null;
  target_system: BlobSinkPromptPreviewTargetSystem;
  prompt_snapshot: string;
  preview_size: number;
  status: BlobSinkPromptPreviewRunStatus;
  total_count: number;
  processed_count: number;
  successful_count: number;
  failed_count: number;
  sampled_matter_ids: string[];
  sampled_blob_ids: string[];
  workflow_id: string | null;
  error_message: string | null;
  created_by_id: string | null;
  created_at: string;
  modified_at: string;
}

export interface BlobSinkPromptPreviewRunDetailsSchema
  extends BlobSinkPromptPreviewRunSchema {
  results: BlobSinkPromptPreviewResultSchema[];
}

export interface CreateBlobSinkPromptPreviewRunSchema {
  prompt: string;
  preview_size?: number;
  base_run_id?: string | null;
}

export interface FirmsBlobSinkConfigsPromptPreviewRunsListApiArg {
  firmId: string;
  configId: string;
}

export interface FirmsBlobSinkConfigsPromptPreviewRunsCreateApiArg {
  firmId: string;
  configId: string;
  createBlobSinkPromptPreviewRunSchema: CreateBlobSinkPromptPreviewRunSchema;
}

export interface FirmsBlobSinkConfigsPromptPreviewRunsLatestApiArg {
  firmId: string;
  configId: string;
}

export interface FirmsBlobSinkConfigsPromptPreviewRunsRetrieveApiArg {
  firmId: string;
  configId: string;
  runId: string;
}

export interface FirmsBlobSinkConfigsPromptPreviewRunsApplyApiArg {
  firmId: string;
  configId: string;
  runId: string;
}

const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes: [BLOB_SINK_PROMPT_PREVIEW_TAG, 'blob-sink-config'],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      firmsBlobSinkConfigsPromptPreviewRunsList: build.query<
        BlobSinkPromptPreviewRunSchema[],
        FirmsBlobSinkConfigsPromptPreviewRunsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/${queryArg.configId}/prompt-preview-runs/`,
        }),
        providesTags: (_result, _error, { configId }) => [
          { type: BLOB_SINK_PROMPT_PREVIEW_TAG, id: configId },
        ],
      }),
      firmsBlobSinkConfigsPromptPreviewRunsCreate: build.mutation<
        BlobSinkPromptPreviewRunSchema,
        FirmsBlobSinkConfigsPromptPreviewRunsCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/${queryArg.configId}/prompt-preview-runs/`,
          method: 'POST',
          body: queryArg.createBlobSinkPromptPreviewRunSchema,
        }),
        invalidatesTags: (_result, _error, { configId }) => [
          { type: BLOB_SINK_PROMPT_PREVIEW_TAG, id: configId },
        ],
      }),
      firmsBlobSinkConfigsPromptPreviewRunsLatest: build.query<
        BlobSinkPromptPreviewRunDetailsSchema,
        FirmsBlobSinkConfigsPromptPreviewRunsLatestApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/${queryArg.configId}/prompt-preview-runs/latest/`,
        }),
        providesTags: (_result, _error, { configId }) => [
          { type: BLOB_SINK_PROMPT_PREVIEW_TAG, id: configId },
        ],
      }),
      firmsBlobSinkConfigsPromptPreviewRunsRetrieve: build.query<
        BlobSinkPromptPreviewRunDetailsSchema,
        FirmsBlobSinkConfigsPromptPreviewRunsRetrieveApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/${queryArg.configId}/prompt-preview-runs/${queryArg.runId}/`,
        }),
        providesTags: (_result, _error, { configId, runId }) => [
          { type: BLOB_SINK_PROMPT_PREVIEW_TAG, id: configId },
          { type: BLOB_SINK_PROMPT_PREVIEW_TAG, id: runId },
        ],
      }),
      firmsBlobSinkConfigsPromptPreviewRunsApply: build.mutation<
        BlobSinkConfigSchema,
        FirmsBlobSinkConfigsPromptPreviewRunsApplyApiArg
      >({
        query: (queryArg) => ({
          url: `/api/firms/${queryArg.firmId}/blob-sink-configs/${queryArg.configId}/prompt-preview-runs/${queryArg.runId}/apply/`,
          method: 'POST',
          body: {},
        }),
        invalidatesTags: (_result, _error, { configId, runId }) => [
          'blob-sink-config',
          { type: BLOB_SINK_PROMPT_PREVIEW_TAG, id: configId },
          { type: BLOB_SINK_PROMPT_PREVIEW_TAG, id: runId },
        ],
      }),
    }),
    overrideExisting: false,
  });

export const blobSinkPromptPreviewService = injectedRtkApi;

export const {
  useFirmsBlobSinkConfigsPromptPreviewRunsListQuery,
  useFirmsBlobSinkConfigsPromptPreviewRunsCreateMutation,
  useFirmsBlobSinkConfigsPromptPreviewRunsLatestQuery,
  useLazyFirmsBlobSinkConfigsPromptPreviewRunsLatestQuery,
  useFirmsBlobSinkConfigsPromptPreviewRunsRetrieveQuery,
  useFirmsBlobSinkConfigsPromptPreviewRunsApplyMutation,
} = injectedRtkApi;
